import React, { useEffect, useState } from 'react';
import { X, AlertCircle, Save, CalendarDays, MapPin } from 'lucide-react';

const PostEditModal = ({ post, isOpen, saving, error, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    content: '',
    eventVenue: '',
    eventDate: '',
    eventBookingLink: ''
  });

  useEffect(() => {
    if (!isOpen || !post) {
      return;
    }

    setFormData({
      title: post.title || '',
      subtitle: post.subtitle || '',
      content: post.content || '',
      eventVenue: post.eventVenue || '',
      eventDate: post.eventDate || '',
      eventBookingLink: post.eventBookingLink || ''
    });
  }, [isOpen, post]);

  if (!isOpen || !post) {
    return null;
  }

  const isArticle = post.postType === 'article';
  const isEvent = post.postType === 'event';

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Edit post</h3>
            <p className="text-sm text-gray-500">Update the published content and save changes.</p>
          </div>
          <button
            type="button"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            onClick={onClose}
            disabled={saving}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          {(isArticle || isEvent) && (
            <input
              type="text"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-lg font-semibold focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder={isEvent ? 'Event title' : 'Article title'}
              value={formData.title}
              onChange={(event) => setFormData((value) => ({ ...value, title: event.target.value }))}
              maxLength={120}
              disabled={saving}
            />
          )}

          {isArticle && (
            <input
              type="text"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="Subtitle"
              value={formData.subtitle}
              onChange={(event) => setFormData((value) => ({ ...value, subtitle: event.target.value }))}
              maxLength={200}
              disabled={saving}
            />
          )}

          {isEvent && (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="relative">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  placeholder="Venue / Location"
                  value={formData.eventVenue}
                  onChange={(event) => setFormData((value) => ({ ...value, eventVenue: event.target.value }))}
                  disabled={saving}
                />
              </div>

              <div className="relative">
                <CalendarDays size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="datetime-local"
                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  value={formData.eventDate}
                  onChange={(event) => setFormData((value) => ({ ...value, eventDate: event.target.value }))}
                  disabled={saving}
                />
              </div>
            </div>
          )}

          {isEvent && (
            <input
              type="url"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="Booking / RSVP link"
              value={formData.eventBookingLink}
              onChange={(event) => setFormData((value) => ({ ...value, eventBookingLink: event.target.value }))}
              disabled={saving}
            />
          )}

          <textarea
            className="min-h-[180px] w-full resize-y rounded-lg border border-gray-200 px-3 py-3 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Write your post..."
            value={formData.content}
            onChange={(event) => setFormData((value) => ({ ...value, content: event.target.value }))}
            disabled={saving}
          />

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-600">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center rounded-lg bg-[#190192] px-4 py-2 text-sm font-medium text-white hover:bg-[#2C0DB5] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={saving}
            >
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostEditModal;
